/**
 * Resolves a canonical `parties.<type>(.<tail>)?` path to a form-field id.
 *
 * The portal flattens parties to `Array<{ type, ...fields }>`; datachecks
 * strips the `parties.<type>` prefix from spec field paths when populating
 * each row, so canonical `parties.buyer.identifiers.gln` lands in the form
 * as `parties.<idx>.identifiers.gln`.
 *
 * Return policy:
 * - `parties.<type>` (no tail) → `parties.<idx>.name` (anchor on the
 *   party-name cell)
 * - `parties.<type>.<tail>` → `parties.<idx>.<tail>` (direct form key)
 * - party type not in flattenedParties → undefined (caller logs and drops)
 * - `flattenedParties` missing → undefined
 * - shape doesn't match the regex → undefined
 *
 * Downstream, `remapAnnotationToFirstCell` will resolve a non-existent
 * tail field to the first cell of the party row, so deep paths that point
 * at fields not in the current uiConfig still anchor somewhere visible.
 */
export const resolvePartyAnnotationItemId = (
  normalizedPath: string,
  flattenedParties: Array<{ type?: string }> | undefined,
): string | undefined => {
  const match = normalizedPath.match(/^parties\.(\w+)(?:\.(.+))?$/);
  if (!match) {
    return undefined;
  }
  const [, partyType, tail] = match;

  if (!flattenedParties) {
    return undefined;
  }
  const index = flattenedParties.findIndex((p) => p.type === partyType);
  if (index === -1) {
    return undefined;
  }

  return tail ? `parties.${index}.${tail}` : `parties.${index}.name`;
};
